/**
 * In-memory mock backend for the Spaces admin UI (OI-24710).
 *
 * OI-24710 is UI-first: the real /api/v1/spaces/* endpoints (OI-24709 /
 * OI-24711 / OI-24713 / OI-24714) don't exist yet, so the UI is built and
 * demoable against this typed store. It enforces the same fail-fast invariants
 * the backend must (no host / unlicensed host) so the error UI is exercised.
 * Toggle via REACT_APP_SPACES_USE_MOCK (see spaces.service.ts).
 */
import {
  SpaceEvent,
  SpaceEventSummary,
  CreateSpaceEventRequest,
  Employer,
  HostableExpert,
  Invite,
  InviteLink,
  EventCancellationResult,
  SessionInput,
  SpaceEventsOverview,
  UpdateSessionRequest,
  UpdateSpaceEventRequest,
  SessionAttendance,
  AttendanceRow,
} from "@src/models/spaces";
import { SpacesApiError } from "@src/services/spacesError";
import { env } from "@src/env";

const LATENCY_MS = 300;

// The real backend stamps SpaceEvent.SchedulerUserId from the authenticated CS
// user and resolves the display name; the mock has no auth context, so it
// attributes events it creates to the current operator. The list is shared, so
// real data will also include other schedulers' names.
const CURRENT_SCHEDULER = "You";

const delay = <T>(value: T): Promise<T> =>
  new Promise((resolve) => setTimeout(() => resolve(value), LATENCY_MS));

const EMPLOYERS: Employer[] = [
  { id: "emp-asml", name: "ASML" },
  { id: "emp-bol", name: "Bol.com" },
  { id: "emp-ing", name: "ING" },
  { id: "emp-klm", name: "KLM" },
  { id: "emp-philips", name: "Philips" },
];

const EXPERTS: HostableExpert[] = [
  {
    userId: "exp-1",
    fullName: "Test Expert One",
    email: "expert.one@example.com",
    licensed: true,
  },
  {
    userId: "exp-2",
    fullName: "Test Expert Two",
    email: "expert.two@example.com",
    licensed: true,
  },
  {
    userId: "exp-3",
    fullName: "Test Expert Three",
    email: "expert.three@example.com",
    licensed: true,
  },
  {
    userId: "exp-4",
    fullName: "Test Expert Four",
    email: "expert.four@example.com",
    licensed: true,
  },
  // Intentionally unlicensed — selecting as host triggers the fail-fast path.
  {
    userId: "exp-5",
    fullName: "Test Expert Five",
    email: "expert.five@example.com",
    licensed: false,
  },
];

const expertById = (userId: string): HostableExpert | undefined =>
  EXPERTS.find((e) => e.userId === userId);

const employerById = (id: string): Employer | undefined =>
  EMPLOYERS.find((e) => e.id === id);

interface MockState {
  events: SpaceEvent[];
  /** Keyed by sessionId (Space) — invites are per session. */
  invites: Record<string, Invite[]>;
  /** Keyed by sessionId (Space) — one link per session. */
  inviteLinks: Record<string, InviteLink>;
  seq: number;
}

const state: MockState = {
  events: [],
  invites: {},
  inviteLinks: {},
  seq: 1,
};

const nextId = (prefix: string): string => `${prefix}-${state.seq++}`;

// Defaults the backend applies when a session omits them.
const DEFAULT_SESSION_TIMEZONE = "Europe/Amsterdam";
const DEFAULT_SESSION_CAPACITY = 100;
const MAX_EXPERTS_PER_SESSION = 3;

// Naive local datetimes; the timezone is carried alongside (mirrors the
// backend contract: startsAt + timeZone separately).
const startIso = (session: SessionInput): string =>
  `${session.date}T${session.startTime}:00`;

/** End is derived: start + duration (mirrors the backend, which computes it). */
const endIso = (session: SessionInput): string => {
  const [hours, minutes] = session.startTime.split(":").map(Number);
  const total = hours * 60 + minutes + session.durationMinutes;
  const endHours = String(Math.floor(total / 60) % 24).padStart(2, "0");
  const endMinutes = String(total % 60).padStart(2, "0");
  return `${session.date}T${endHours}:${endMinutes}:00`;
};

/** Validates the fail-fast invariants the backend will also enforce. */
const assertCreatable = (request: CreateSpaceEventRequest): void => {
  request.sessions.forEach((session, sessionIndex) => {
    const hosts = session.experts.filter((e) => e.role === "host");
    if (hosts.length === 0) {
      throw new SpacesApiError({
        code: "NO_HOST",
        message: `Session ${sessionIndex + 1} needs at least one host.`,
        sessionIndex,
      });
    }
    if (session.experts.length > MAX_EXPERTS_PER_SESSION) {
      throw new SpacesApiError({
        code: "VALIDATION",
        message: `Session ${sessionIndex + 1} can have at most ${MAX_EXPERTS_PER_SESSION} people, host included.`,
        sessionIndex,
      });
    }
    // Roles that become Zoom alternative hosts must be licensed: hosts always,
    // plus co-hosts for a group session (a meeting has no unlicensed role).
    const unlicensed = session.experts
      .filter((e) => e.role === "host" || e.role === "cohost")
      .map((e) => expertById(e.userId))
      .find((e) => e && !e.licensed);
    if (unlicensed) {
      throw new SpacesApiError({
        code: "UNLICENSED_HOST",
        message: `${unlicensed.email} has no provisioned Zoom licence; ask ops to provision it.`,
        sessionIndex,
        email: unlicensed.email,
      });
    }
  });
};

export const mockGetEmployers = (): Promise<Employer[]> => delay(EMPLOYERS);

export const mockGetHostableExperts = (): Promise<HostableExpert[]> =>
  delay(EXPERTS);

export const mockCreateEvent = async (
  request: CreateSpaceEventRequest,
): Promise<SpaceEvent> => {
  assertCreatable(request);
  const employers = request.employerIds
    .map((employerId) => employerById(employerId))
    .filter((employer): employer is Employer => employer !== undefined);
  const event: SpaceEvent = {
    id: nextId("evt"),
    title: request.title,
    description: request.description,
    eventType: request.eventType,
    availableLanguageCodes: [],
    publicPageUrls: [],
    employers,
    employerName:
      request.employerDisplayName ?? employers[0]?.name ?? "Unknown",
    employerDisplayName: request.employerDisplayName,
    audience: "private",
    scheduledBy: CURRENT_SCHEDULER,
    surveyUrl: request.surveyUrl,
    createdAt: new Date().toISOString(),
    languageCode: null,
    translations: [],
    sessions: request.sessions.map((session) => ({
      id: nextId("ses"),
      // Space.Title is NOT NULL — default to the event title when unnamed.
      title: session.name?.trim() || request.title,
      titleIsDefaulted: !session.name?.trim(),
      startsAt: startIso(session),
      endsAt: endIso(session),
      timeZone: session.timeZone ?? DEFAULT_SESSION_TIMEZONE,
      capacity: session.capacity ?? DEFAULT_SESSION_CAPACITY,
      sessionInstructions: session.sessionInstructions?.trim() || null,
      externalWebinarId: `zoom-${state.seq}`,
      translations: [],
      status: "scheduled",
      experts: session.experts.map((e) => ({
        userId: e.userId,
        fullName: expertById(e.userId)?.fullName ?? "Unknown",
        role: e.role,
      })),
    })),
  };
  state.events.unshift(event);
  return delay(event);
};

export const mockGetEvents = (): Promise<SpaceEventsOverview> => {
  const summaries: SpaceEventSummary[] = state.events.map((event) => {
    const starts = event.sessions.map((s) => s.startsAt).sort();
    const ends = event.sessions.map((s) => s.endsAt).sort();
    return {
      id: event.id,
      title: event.title,
      eventType: event.eventType,
      employerName: event.employerName,
      employerDisplayName: null,
      scheduledBy: event.scheduledBy,
      sessionCount: event.sessions.length,
      nextSessionAt: starts[0] ?? null,
      lastSessionEndsAt: ends.at(-1) ?? null,
    };
  });

  // Cancellation is derived (every session cancelled) and outranks the
  // time-based buckets, mirroring the backend.
  const isCancelled = (summary: SpaceEventSummary): boolean => {
    const event = state.events.find((e) => e.id === summary.id);
    return (
      !!event &&
      event.sessions.length > 0 &&
      event.sessions.every((s) => s.status === "cancelled")
    );
  };
  const cancelled = summaries
    .filter(isCancelled)
    .sort((a, b) =>
      (b.lastSessionEndsAt ?? "").localeCompare(a.lastSessionEndsAt ?? ""),
    );
  const active = summaries.filter((summary) => !isCancelled(summary));

  // Mirrors the backend split: past once the latest session has ended; events
  // with no sessions stay upcoming.
  const now = Date.now();
  const isPast = (summary: SpaceEventSummary): boolean =>
    summary.lastSessionEndsAt !== null &&
    new Date(summary.lastSessionEndsAt).getTime() < now;

  const upcoming = active
    .filter((summary) => !isPast(summary))
    .sort((a, b) =>
      (a.nextSessionAt ?? "9999").localeCompare(b.nextSessionAt ?? "9999"),
    );
  const past = active
    .filter(isPast)
    .sort((a, b) =>
      (b.lastSessionEndsAt ?? "").localeCompare(a.lastSessionEndsAt ?? ""),
    );

  return delay({ upcoming, past, cancelled });
};

export const mockUpdateEvent = async (
  id: string,
  request: UpdateSpaceEventRequest,
): Promise<void> => {
  const event = state.events.find((e) => e.id === id);
  if (!event) {
    throw new SpacesApiError({
      code: "VALIDATION",
      message: "Event not found.",
    });
  }
  const newTitle = request.title.trim();
  // A rename cascades to sessions whose title was defaulted from the event
  // title; explicitly named sessions keep their name (OI-25024).
  if (newTitle !== event.title) {
    event.sessions
      .filter((session) => session.titleIsDefaulted)
      .forEach((session) => {
        session.title = newTitle;
      });
  }
  event.title = newTitle;
  event.description = request.description?.trim() || null;
  event.surveyUrl = request.surveyUrl?.trim() || null;
  return delay(undefined);
};

export const mockUpdateSession = async (
  sessionId: string,
  request: UpdateSessionRequest,
): Promise<void> => {
  const event = state.events.find((e) =>
    e.sessions.some((s) => s.id === sessionId),
  );
  const session = event?.sessions.find((s) => s.id === sessionId);
  if (!event || !session) {
    throw new SpacesApiError({
      code: "VALIDATION",
      message: "Session not found.",
    });
  }
  if (session.status === "cancelled") {
    throw new SpacesApiError({
      code: "SESSION_CANCELLED",
      message: "Cancelled sessions cannot be edited.",
    });
  }
  if (new Date(session.endsAt).getTime() <= Date.now()) {
    throw new SpacesApiError({
      code: "SESSION_COMPLETED",
      message: "Completed sessions cannot be edited.",
    });
  }
  session.title = request.name?.trim() || event.title;
  session.titleIsDefaulted = !request.name?.trim();
  session.capacity = request.capacity;
  session.sessionInstructions = request.sessionInstructions?.trim() || null;
  return delay(undefined);
};

export const mockCancelSession = async (sessionId: string): Promise<void> => {
  const event = state.events.find((e) =>
    e.sessions.some((s) => s.id === sessionId),
  );
  const session = event?.sessions.find((s) => s.id === sessionId);
  if (!session) {
    throw new SpacesApiError({
      code: "VALIDATION",
      message: "Session not found.",
    });
  }
  if (session.status === "cancelled") return delay(undefined); // idempotent
  if (new Date(session.endsAt).getTime() <= Date.now()) {
    throw new SpacesApiError({
      code: "SESSION_COMPLETED",
      message: "Completed sessions cannot be cancelled.",
    });
  }
  session.status = "cancelled";
  return delay(undefined);
};

export const mockCancelEvent = async (
  eventId: string,
): Promise<EventCancellationResult> => {
  const event = state.events.find((e) => e.id === eventId);
  if (!event) {
    throw new SpacesApiError({
      code: "VALIDATION",
      message: "Event not found.",
    });
  }
  // Mirror the backend: only upcoming scheduled sessions are cancelled.
  const cancellable = event.sessions.filter(
    (s) =>
      s.status === "scheduled" && new Date(s.endsAt).getTime() > Date.now(),
  );
  cancellable.forEach((s) => {
    s.status = "cancelled";
  });
  return delay({ cancelledSessionIds: cancellable.map((s) => s.id) });
};

export const mockGetEvent = async (id: string): Promise<SpaceEvent> => {
  const event = state.events.find((e) => e.id === id);
  if (!event) {
    throw new SpacesApiError({
      code: "VALIDATION",
      message: "Event not found.",
    });
  }
  return delay(event);
};

export const mockGetInvites = (sessionId: string): Promise<Invite[]> =>
  delay(state.invites[sessionId] ?? []);

export const mockCreateInvites = (
  sessionId: string,
  emails: string[],
): Promise<Invite[]> => {
  const existing = state.invites[sessionId] ?? [];
  const known = new Set(existing.map((i) => i.email.toLowerCase()));
  const added = emails
    .filter((email) => !known.has(email.toLowerCase()))
    .map<Invite>((email) => ({
      id: nextId("inv"),
      email,
      status: "sent",
      sentAt: new Date().toISOString(),
    }));
  state.invites[sessionId] = [...existing, ...added];
  return delay(state.invites[sessionId]);
};

export const mockGetInviteLink = (
  sessionId: string,
): Promise<InviteLink | null> => delay(state.inviteLinks[sessionId] ?? null);

export const mockCreateInviteLink = (
  sessionId: string,
  seatCap: number | null,
): Promise<InviteLink> => {
  const link: InviteLink = {
    // Mirrors what the API mints: the MOU handler at `{MOU}/spaces/invite/{token}`, not a route in
    // this app. This app has no invitee-facing surface, so a link back into it would never resolve.
    // One entry only: the mock has no per-language domains to hand out.
    urls: [
      {
        languageCode: "",
        url: `${env.REACT_APP_MOU_BASE_URL.replace(/\/$/, "")}/spaces/invite/mock-${sessionId}-${state.seq++}`,
      },
    ],
    seatCap,
    registeredCount: 0,
    createdAt: new Date().toISOString(),
  };
  state.inviteLinks[sessionId] = link;
  return delay(link);
};

export const mockRevokeInviteLink = (sessionId: string): Promise<void> => {
  delete state.inviteLinks[sessionId];
  return delay(undefined);
};

/** Title-cases an email local-part into a display name (mock has no real names). */
const nameFromEmail = (email: string): string => {
  const local = email.split("@")[0] ?? email;
  return (
    local
      .split(/[._-]+/)
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ") || email
  );
};

/**
 * Registration + attendance summary for a session. Registration data is
 * derived from the invite funnel; attendance outcomes are null because no
 * session has run in the mock. The real backend reads SpaceUser seats and,
 * once ingested, Zoom participant reports (S5).
 */
export const mockGetSessionAttendance = (
  sessionId: string,
): Promise<SessionAttendance> => {
  const event = state.events.find((e) =>
    e.sessions.some((s) => s.id === sessionId),
  );
  const session = event?.sessions.find((s) => s.id === sessionId);
  if (!event || !session) {
    throw new SpacesApiError({
      code: "VALIDATION",
      message: "Session not found.",
    });
  }

  const invites = state.invites[sessionId] ?? [];
  const invited = invites.length;
  const opened = invites.filter(
    (i) => i.status === "opened" || i.status === "registered",
  ).length;
  const registered = invites.filter((i) => i.status === "registered").length;
  const cancelled = invites.filter((i) => i.status === "cancelled").length;

  // Roster = people who hold a seat (registered, or a since-cancelled
  // registration). The real backend reads SpaceUser rows; the mock
  // approximates from invite status.
  const roster: AttendanceRow[] = invites
    .filter((i) => i.status === "registered" || i.status === "cancelled")
    .map(
      (invite): AttendanceRow => ({
        name: nameFromEmail(invite.email),
        email: invite.email,
        employer: event.employerName,
        registeredAt: invite.sentAt,
        status: invite.status === "cancelled" ? "cancelled" : "registered",
        joinedAt: null,
        durationSeconds: null,
        attendanceRatePercent: null,
      }),
    );

  return delay({
    session: {
      id: session.id,
      title: session.title,
      startsAt: session.startsAt,
      endsAt: session.endsAt,
      timeZone: session.timeZone,
      status: session.status,
    },
    counts: {
      invited,
      registered,
      cancelled,
      attended: null,
      noShow: null,
      avgDurationSeconds: null,
    },
    funnel: { invited, opened, registered },
    roster,
  });
};
